import React, { useEffect, useState } from 'react';
import { useUserData } from '../contexts/UserDataContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

const grammarTopics = [
    { key: 'noun', label: 'Noun' },
    { key: 'verb', label: 'Verb' },
    { key: 'adjective', label: 'Adjective' },
    { key: 'adverb', label: 'Adverb' },
    { key: 'pronoun', label: 'Pronoun' },
    { key: 'preposition', label: 'Preposition' },
    { key: 'conjunction', label: 'Conjunction' },
    { key: 'interjection', label: 'Interjection' },
    { key: 'presentSimple', label: 'Present Simple' },
    { key: 'presentContinuous', label: 'Present Continuous' },
    { key: 'presentPerfect', label: 'Present Perfect' },
    { key: 'pastSimple', label: 'Past Simple' },
    { key: 'pastContinuous', label: 'Past Continuous' },
    { key: 'pastPerfect', label: 'Past Perfect' },
    { key: 'futureSimple', label: 'Future Simple' },
    { key: 'futureContinuous', label: 'Future Continuous' },
    { key: 'futurePerfect', label: 'Future Perfect' },
    { key: 'voiceNarration', label: 'Voice & Narration' },
    { key: 'modals', label: 'Modals' },
    { key: 'auxiliaries', label: 'Auxiliaries' },
];

export default function FavoritesGrammarPage() {
    const { getFavoriteGrammar, userData, removeFavoriteGrammar } = useUserData();
    const { currentUser } = useAuth();
    const [selectedTopic, setSelectedTopic] = useState(grammarTopics[0].key);
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [userCount, setUserCount] = useState<number>(0);
    const PAGE_SIZE = 5;
    const [page, setPage] = useState(0);

    // Debug logs at the top
    console.log('FavoritesGrammarPage - userData:', userData);
    console.log('FavoritesGrammarPage - selectedLanguage:', userData?.selectedLanguage);
    console.log('FavoritesGrammarPage - userCount:', userCount);
    console.log('FavoritesGrammarPage - items:', items);

    useEffect(() => {
        setLoading(true);
        getFavoriteGrammar(selectedTopic)
            .then(res => {
                setItems(res || []);
                setPage(0); // Reset to first page when topic changes
                // Debug log after API call
                console.log('FavoritesGrammarPage - API items:', res);
            })
            .finally(() => setLoading(false));
        // Remove the unnecessary count API call
        // if (currentUser?.uid) {
        //     api.getFavoriteGrammarCount(currentUser.uid, selectedTopic)
        //         .then(count => {
        //             setUserCount(count);
        //             // Debug log after count API call
        //             console.log('FavoritesGrammarPage - API userCount:', count);
        //         })
        //         .catch(() => setUserCount(0));
        // }
    }, [selectedTopic, getFavoriteGrammar, currentUser?.uid]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-pink-100 p-6">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-4xl font-extrabold tracking-tight drop-shadow-lg mb-6 text-center">
                    <span className="bg-gradient-to-r from-pink-500 via-yellow-500 to-pink-400 bg-clip-text text-transparent">Favorite Grammar</span>
                </h1>
            </div>
            <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                    {/* Removed Selected Language text */}
                    <div className="text-lg font-bold text-pink-700">Total: {items.length}</div>
                </div>
                <div className="flex flex-wrap gap-2 mb-6">
                    {grammarTopics.map(topic => (
                        <button
                            key={topic.key}
                            className={`px-4 py-2 rounded-full font-medium transition-colors ${selectedTopic === topic.key ? 'bg-pink-600 text-white' : 'bg-pink-100 text-pink-800 hover:bg-pink-200'}`}
                            onClick={() => setSelectedTopic(topic.key)}
                        >
                            {topic.label}
                        </button>
                    ))}
                </div>
                {loading ? (
                    <div className="text-center text-lg text-pink-500 font-semibold py-12">Loading...</div>
                ) : items.length === 0 ? (
                    <div className="text-center text-gray-500 font-semibold py-12">No favorite grammar items in this topic!</div>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-6 justify-center items-stretch">
                            {items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((item, idx) => (
                                <div key={item.itemId || idx} className="flex-1 max-w-xs min-w-[250px] bg-white/80 backdrop-blur-2xl border border-pink-200 rounded-3xl p-6 shadow-xl flex flex-col items-center gap-4 relative">
                                    <div className="w-full text-center mb-2">
                                        {/* Show selected language on top, English below if not English */}
                                        <div className="text-2xl font-bold mb-2 text-gray-900">
                                            {item[userData?.selectedLanguage || 'en'] || item.en || <span className='italic text-red-500'>Grammar example not found</span>}
                                        </div>
                                        {userData?.selectedLanguage !== 'en' && item.en && (
                                            <div className="text-base text-gray-500">{item.en}</div>
                                        )}
                                    </div>
                                    {item.translation && (
                                        <div className="text-lg text-pink-700 font-semibold mb-2">{item.translation}</div>
                                    )}
                                    {item.subtopic && (
                                        <div className="text-base text-gray-500 italic mb-2">{item.subtopic}</div>
                                    )}
                                    <button
                                        className="flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r from-red-400 to-red-600 text-white font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-300"
                                        onClick={async () => {
                                            await removeFavoriteGrammar(item.itemId);
                                            setItems(prev => prev.filter((_, i) => i !== idx));
                                        }}
                                    >
                                        <span>🗑️</span> Unfavorite
                                    </button>
                                </div>
                            ))}
                        </div>
                        {/* Pagination controls */}
                        {items.length > PAGE_SIZE && (
                            <div className="flex justify-center gap-4 mt-6">
                                <button
                                    className="px-4 py-2 rounded-full bg-gray-200 text-gray-700 font-semibold disabled:opacity-50"
                                    onClick={() => setPage(p => Math.max(0, p - 1))}
                                    disabled={page === 0}
                                >
                                    Previous
                                </button>
                                <span className="px-4 py-2 text-gray-700 font-semibold">Page {page + 1} of {Math.ceil(items.length / PAGE_SIZE)}</span>
                                <button
                                    className="px-4 py-2 rounded-full bg-pink-500 text-white font-semibold disabled:opacity-50"
                                    onClick={() => setPage(p => (p + 1 < Math.ceil(items.length / PAGE_SIZE) ? p + 1 : p))}
                                    disabled={page + 1 >= Math.ceil(items.length / PAGE_SIZE)}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
} 